/**
 * LOD0 Brand Image viewer geometry (PURE, deterministic, renderer-agnostic).
 *
 * Owns the transforms between the CURRENT operational Territory geometry (a set
 * of world-space parcel rectangles) and the screen-space draw + EXACT-UNION
 * clip primitives the renderer consumes. Nothing here touches a canvas context,
 * so every rule (aspect fit, safe margin, union membership, exact clip rects)
 * is unit-testable without a DOM. The renderer does the thin ctx plumbing only.
 *
 * FROZEN law: the image is fit (aspect-preserving, margin-inset, centred) into
 * the CURRENT territory BOUNDING box, but the drawn pixels are clipped to the
 * EXACT union of the parcel rectangles — never the bounding box. Holes and
 * L-shapes fall out naturally because a gap covered by no parcel rect is simply
 * absent from the clip path.
 */
import type { Bounds } from "../domain/lod0-brand-image";
import type { Camera } from "./camera";
import { worldToScreen } from "./camera";
import {
  buildTerritories,
  normalizeOwnerIdentity,
  type PropertyUnit,
} from "../domain/territory";

/** A world- or screen-space axis-aligned rectangle. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Minimal parcel shape the layer needs (a subset of viewer `Parcel`). */
export interface UnitRect {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  owner: string;
}

/** Bounding box of the union of `units` in world coords, or null if empty. */
export function unionBoundsWorld(units: readonly Rect[]): Bounds | null {
  if (units.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const u of units) {
    if (u.w <= 0 || u.h <= 0) continue;
    if (u.x < minX) minX = u.x;
    if (u.y < minY) minY = u.y;
    if (u.x + u.w > maxX) maxX = u.x + u.w;
    if (u.y + u.h > maxY) maxY = u.y + u.h;
  }
  if (!Number.isFinite(minX) || maxX <= minX || maxY <= minY) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * EXACT union membership (half-open geometry: x∈[x,x+w), y∈[y,y+h)). A point in
 * a hole (covered by no parcel) is NOT in the union even if inside the bounding
 * box. This is the authoritative predicate the pixel clip must honour.
 */
export function pointInUnionWorld(units: readonly Rect[], wx: number, wy: number): boolean {
  for (const u of units) {
    if (u.w <= 0 || u.h <= 0) continue;
    if (wx >= u.x && wx < u.x + u.w && wy >= u.y && wy < u.y + u.h) return true;
  }
  return false;
}

/** Per-parcel screen-space rectangles that COMPOSE the exact union clip path. */
export function unionScreenRects(camera: Camera, units: readonly Rect[]): Rect[] {
  const out: Rect[] = [];
  for (const u of units) {
    if (u.w <= 0 || u.h <= 0) continue;
    const a = worldToScreen(camera, u.x, u.y);
    const b = worldToScreen(camera, u.x + u.w, u.y + u.h);
    out.push({ x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y });
  }
  return out;
}

export interface BrandImageDrawGeometry {
  /** Screen-space envelope the image is drawn into — the FULL territory
   * bounding box (the image is stretched to fill every parcel edge-to-edge;
   * the exact-union clip below restricts the painted pixels to real parcels). */
  fit: Rect;
  /** Screen-space rectangles forming the EXACT union clip (never a bbox). */
  clip: Rect[];
}

/**
 * Compute the screen-space draw + clip geometry for a brand image on the CURRENT
 * territory `units`. The image FILLS the entire territory bounding box (stretch,
 * no aspect margin) and is clipped to the EXACT parcel union so it occupies the
 * whole footprint of every parcel it is installed on. Returns null when the
 * territory has no drawable area (degenerate zoom / image dimensions).
 */
export function computeBrandImageDrawGeometry(
  camera: Camera,
  units: readonly Rect[],
  imageWidth: number,
  imageHeight: number,
): BrandImageDrawGeometry | null {
  const wb = unionBoundsWorld(units);
  if (!wb) return null;
  if (imageWidth <= 0 || imageHeight <= 0) return null;
  const tl = worldToScreen(camera, wb.x, wb.y);
  const fit: Rect = { x: tl.x, y: tl.y, w: wb.w * camera.scale, h: wb.h * camera.scale };
  if (fit.w <= 0 || fit.h <= 0) return null;
  return { fit, clip: unionScreenRects(camera, units) };
}

// ── Discovery association (pure) ─────────────────────────────────────────────

/** A persistent identity discovered for the loaded parcels. */
export interface DiscoveredIdentity {
  identityId: string;
  owner: string;
  /** binding member parcel ids (the identity anchor, subset of the current). */
  memberParcelIds: number[];
}

/** A ready-to-render territory: the CURRENT component + the image dimensions. */
export interface RenderableTerritory {
  identityId: string;
  owner: string;
  /** CURRENT operational component units (world rects) — grows on expansion. */
  units: UnitRect[];
  imageWidth: number;
  imageHeight: number;
}

function toPropertyUnits(parcels: readonly UnitRect[]): PropertyUnit[] {
  return parcels.map((p) => ({ id: String(p.id), owner: p.owner, x: p.x, y: p.y, w: p.w, h: p.h }));
}

/**
 * Associate each discovered identity that HAS a brand image to the CURRENT
 * connected same-owner component that contains ALL of its binding parcels,
 * derived from the loaded parcels via the canonical WO#1 territory authority.
 *
 * Deterministic: identities are processed in ascending id order; an identity
 * whose binding parcels are not all currently loaded is skipped (fail-soft, no
 * bounding-box guess). This makes expansion recentre automatically (the current
 * component is larger than the binding) with zero persisted position.
 */
export function associateRenderables(
  parcels: readonly UnitRect[],
  identities: readonly DiscoveredIdentity[],
  imageDims: ReadonlyMap<string, { width: number; height: number }>,
): RenderableTerritory[] {
  if (parcels.length === 0 || identities.length === 0 || imageDims.size === 0) return [];
  const byId = new Map(parcels.map((p) => [p.id, p]));
  const territories = buildTerritories(toPropertyUnits(parcels));
  // Index each component's member ids for containment checks.
  const components = territories.map((t) => ({
    owner: t.owner,
    ids: new Set(t.units.map((u) => Number(u.id))),
  }));

  const out: RenderableTerritory[] = [];
  const sorted = [...identities].sort((a, b) => a.identityId.localeCompare(b.identityId));
  for (const ident of sorted) {
    const dims = imageDims.get(ident.identityId);
    if (!dims) continue; // only identities with a brand image render
    const ownerKey = normalizeOwnerIdentity(ident.owner);
    const binding = ident.memberParcelIds;
    if (binding.length === 0) continue;
    if (!binding.every((pid) => byId.has(pid))) continue; // not fully loaded → skip
    const comp = components.find(
      (c) => c.owner === ownerKey && binding.every((pid) => c.ids.has(pid)),
    );
    if (!comp) continue;
    const units: UnitRect[] = [];
    for (const pid of comp.ids) {
      const p = byId.get(pid);
      if (p) units.push(p);
    }
    if (units.length === 0) continue;
    out.push({
      identityId: ident.identityId,
      owner: ownerKey,
      units,
      imageWidth: dims.width,
      imageHeight: dims.height,
    });
  }
  return out;
}
